import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CalendarDays, Sparkles, Trash2, ChevronRight, Dumbbell, Crown } from "lucide-react";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const REST = "__rest__";

export default function Programs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [programs, setPrograms] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [title, setTitle] = useState("My Weekly Plan");
  const [days, setDays] = useState({});
  const [asTemplate, setAsTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);

  const load = useCallback(() => {
    api.get("/programs").then(({ data }) => setPrograms(data)).catch(() => {});
    api.get("/workouts").then(({ data }) => setWorkouts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const wName = (id) => workouts.find((w) => w.id === id)?.title || "Workout";
  const setDay = (d, v) => setDays((prev) => ({ ...prev, [d]: v === REST ? null : v }));

  const autoBuild = async () => {
    setBuilding(true);
    try {
      const { data } = await api.post("/programs/auto-build", { days_per_week: 4 });
      setDays(data.days || {});
      toast.success("Balanced week built — tweak any day you like");
    } catch (e) { toast.error("Could not auto-build"); }
    finally { setBuilding(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/programs", { title: title.trim() || "My Weekly Plan", days, is_template: isAdmin && asTemplate });
      toast.success(asTemplate ? "Program published" : "Plan saved");
      setDays({}); setTitle("My Weekly Plan"); setAsTemplate(false);
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    try { await api.delete(`/programs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Delete failed"); }
  };

  // Balance indicator: distinct movement patterns across the assigned days.
  const assigned = Object.values(days).filter(Boolean);
  const patternSet = new Set();
  assigned.forEach((id) => (workouts.find((w) => w.id === id)?.movement_patterns || []).forEach((p) => patternSet.add(p)));
  const trainingDays = assigned.length;

  return (
    <AppShell title="Programs">
      <div className="space-y-5" data-testid="programs-page">
        {/* Builder */}
        <Card className="rounded-2xl bg-card border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="h-5 w-5 text-[#ccff0c]" />
            <p className="font-display text-lg font-bold">Build your week</p>
          </div>
          <div className="mb-3">
            <Label className="text-xs">Plan name</Label>
            <Input data-testid="program-title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-card border-border h-10 mt-1" />
          </div>
          <Button data-testid="auto-build-btn" onClick={autoBuild} disabled={building} className="w-full mb-3 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
            <Sparkles className="h-4 w-4 mr-1" /> {building ? "Building…" : "Auto-build a balanced week"}
          </Button>

          <div className="space-y-2">
            {DAYS.map(([d, label]) => (
              <div key={d} className="flex items-center gap-2" data-testid={`day-row-${d}`}>
                <span className="w-10 text-sm font-semibold text-muted-foreground">{label}</span>
                <Select value={days[d] || REST} onValueChange={(v) => setDay(d, v)}>
                  <SelectTrigger data-testid={`day-select-${d}`} className="bg-card border-border h-10 flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border text-foreground max-h-[300px]">
                    <SelectItem value={REST}>Rest day</SelectItem>
                    {workouts.map((w) => <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {/* Balance indicator */}
          <div className="mt-3 flex items-center justify-between rounded-xl bg-muted border border-border p-3">
            <div>
              <p className="text-xs text-muted-foreground">Weekly balance</p>
              <p className="text-sm font-semibold">{trainingDays} training day{trainingDays === 1 ? "" : "s"} · {patternSet.size} pattern{patternSet.size === 1 ? "" : "s"}</p>
            </div>
            <Badge data-testid="balance-badge" className={patternSet.size >= 5 ? "bg-[#ccff0c] text-black" : "bg-secondary border border-border text-muted-foreground"}>
              {patternSet.size >= 5 ? "Well balanced" : patternSet.size >= 3 ? "Decent" : "Add variety"}
            </Badge>
          </div>

          {isAdmin && (
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-1.5"><Crown className="h-4 w-4 text-[#ccff0c]" /><span className="text-sm">Publish as shared template</span></div>
              <Switch data-testid="publish-template" checked={asTemplate} onCheckedChange={setAsTemplate} />
            </div>
          )}

          <Button data-testid="save-program" onClick={save} disabled={saving || trainingDays === 0} className="w-full mt-3 bg-foreground text-background hover:bg-foreground/90 font-semibold">
            {saving ? "Saving…" : "Save plan"}
          </Button>
        </Card>

        {/* Existing programs */}
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Your plans &amp; programs</p>
          {programs.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No programs yet. Build your first week above.</p>
          ) : (
            <div className="space-y-2">
              {programs.map((p) => {
                const count = Object.values(p.days || {}).filter(Boolean).length;
                return (
                  <Card key={p.id} data-testid={`program-${p.id}`} className="rounded-2xl bg-card border-border p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0" onClick={() => navigate(`/programs/${p.id}`)} role="button">
                        <Dumbbell className="h-5 w-5 text-[#ccff0c] flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{p.title}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {p.is_template && <Badge className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20 text-[10px]">Program</Badge>}
                            <span className="text-xs text-muted-foreground">{count} day{count === 1 ? "" : "s"}/week</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {(!p.is_template || isAdmin) && (
                          <button data-testid={`delete-program-${p.id}`} onClick={() => del(p.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => navigate(`/programs/${p.id}`)} className="p-1 text-muted-foreground"><ChevronRight className="h-5 w-5" /></button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
